import React, { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { APP_PROXY_URL } from '../../utils/constants';
import { formatFrequency, getIdFromGid } from '../utils';
import Button from '../../components/Button';
import Breakpoints from '../../design-utils/breakpoints';

const variantImgDivStyle = (imgUrl) =>
  imgUrl
    ? {
        backgroundImage: `url('${imgUrl}')`,
      }
    : { backgroundColor: '#E5E5E5' };

const nextOrderFormat = (NextBillingDate) => {
  const date = dayjs(NextBillingDate);
  return { mmm: date.format('MMM'), dd: date.format('DD') };
};

const SubscriptionLine = ({
  line,
}: {
  line: LineItem;
}): React.ReactElement | null => {
  const { t } = useTranslation();

  if (line.HideLine) return null;

  return (
    <Line
      className="bundleapp-subscription-list-item__subscription-line"
      key={line.ProductId}
    >
      <LineCol1 className="bundleapp-subscription-list-item__subscription-line-c1">
        <VariantThumb
          style={variantImgDivStyle(line.VariantImageSrc || null)}
          className="bundleapp-subscription-list-item__variant-thumb"
          title="Variant Thumbnail"
        />
      </LineCol1>
      <LineCol2 className="bundleapp-subscription-list-item__subscription-line-c2">
        <ProductTitle className="bundleapp-subscription-list-item__product-title">
          <span>{line.Title}</span>
        </ProductTitle>
        <ProductVariantQty className="bundleapp-subscription-list-item__product-variant-qty">
          {line.VariantTitle && line.VariantTitle !== '-' ? (
            <>
              <span>{line.VariantTitle}</span>
              <span> • </span>
            </>
          ) : null}
          <span>{line.Quantity ? `${t('subscription.qty')} ` : ''}</span>
          <span>{line.Quantity ? line.Quantity : ''}</span>
        </ProductVariantQty>
      </LineCol2>
    </Line>
  );
};

// TODO: clean and anstract these styles
const Line = styled.div`
  display: flex;
  margin-bottom: 22px;
`;

const VariantThumb = styled.div`
  display: inline-block;
  width: var(--variantThumbWidth);
  height: var(--varianThumbHeight);
  background-position: center center;
  background-size: cover;
  border-radius: var(--variantThumbBorderRadius);
`;

const LineCol1 = styled.div`
  margin-right: 17px;
  display: flex;
`;

const LineCol2 = styled.div`
  display: flex;
  flex-direction: column;
  align-content: space-between;
`;

const ProductTitle = styled.div`
  font-weight: var(--productTitleFontWeight);
  font-size: var(--productTitleFontSize);
  line-height: var(--productTitleLineHeight);
`;

const ProductVariantQty = styled.div`
  font-size: var(--productVariantQtyFontSize);
  line-height: var(--productVariantQtyLineHeight);
`;

export default ({
  subscription,
}: {
  subscription: Subscription;
}): React.ReactElement => {
  const { t } = useTranslation();

  const navigateToManageSubscription = useCallback(() => {
    const subscriptionId = getIdFromGid(subscription.SubscriptionId);
    const searchParams = new URLSearchParams(window.location.search);
    if (subscriptionId) searchParams.set('subscriptionId', subscriptionId);

    // This removes the last slash if it exists. eg: /fr/ -> /fr and /fr -> /fr
    const shopifyRoot =
      (window.Shopify?.routes?.root || '/').replace(/(.*)(\/)$/, '$1') || '';
    const url = `${shopifyRoot}${APP_PROXY_URL}?${searchParams.toString()}`;

    window.location.href = url;
  }, [subscription]);

  const isLocalPickUp = !isEmpty(subscription.DeliveryMethod?.pickupOption);
  const address = isLocalPickUp
    ? subscription.DeliveryMethod?.pickupOption?.location?.address
    : subscription.DeliveryMethod?.address;

  return (
    <ListItem className="bundleapp-subscription-list-item">
      <Row1 className="bundleapp-subscription-list-item__r1">
        <Row1Col1 className="bundleapp-subscription-list-item__r1c1">
          {subscription.SubscriptionStatus === 'ACTIVE' ? (
            <>
              <BillingMonth className="bundleapp-subscription-list-item__billing-month">
                <span>{nextOrderFormat(subscription.NextBillingDate).mmm}</span>
              </BillingMonth>
              <BillingDay className="bundleapp-subscription-list-item__billing-day">
                <span>{nextOrderFormat(subscription.NextBillingDate).dd}</span>
              </BillingDay>
            </>
          ) : (
            <div className="bundleapp-subscription-list-item__billing-date-none">
              <span>―</span>
            </div>
          )}
        </Row1Col1>
        <Row1Col2 className="bundleapp-subscription-list-item__r1c2">
          {subscription.Lines?.map((line, i) => (
            <SubscriptionLine key={i} line={line} />
          ))}
        </Row1Col2>
        <Row1Col3 className="bundleapp-subscription-list-item__r1c3">
          {subscription.SubscriptionStatus !== 'ACTIVE' ? (
            <NotActiveStatus
              className={
                'bundleapp-subscription-list-item__subscription-not-active-status'
              }
            >
              {subscription.SubscriptionStatus === 'PAUSED'
                ? t('subscription.paused')
                : subscription.SubscriptionStatus === 'EXPIRED'
                  ? t('subscription.expired')
                  : subscription.SubscriptionStatus === 'CANCELLED'
                    ? t('subscription.cancelled')
                    : subscription.SubscriptionStatus.toLowerCase()}
            </NotActiveStatus>
          ) : subscription.OriginalNextBillingDate ? (
            <>{t('subscription.delayed')}</>
          ) : (
            <>
              <TotalPrice
                className={
                  'bundleapp-subscription-list-item__subscription-total-price'
                }
              >
                <span>{subscription.FormattedSubscriptionTotal}</span>
              </TotalPrice>
              <DeliveryFrequency
                className={
                  'bundleapp-subscription-list-item__subscription-delivery-frequency'
                }
              >
                <span>
                  {formatFrequency(
                    t,
                    subscription.DeliveryIntervalDeliveryCount,
                    subscription.FormattedFrequency,
                  )}
                </span>
              </DeliveryFrequency>
            </>
          )}
        </Row1Col3>
      </Row1>
      <Row2 className="bundleapp-subscription-list-item__r2">
        <Row2Col1 className="bundleapp-subscription-list-item__r2c1"></Row2Col1>
        <Row2Col2 className="bundleapp-subscription-list-item__r2c2">
          <div className="bundleapp-subscription-list-item__ships-to">
            <span>
              {`${t(
                `subscription.${isLocalPickUp ? 'local_pickup' : 'ships_to'}`,
              )}: `}{' '}
            </span>
          </div>
          {address && (
            <>
              <div className="bundleapp-subscription-list-item__shipping-address-first">
                <ShippingAddress>
                  {address?.address1}{' '}
                  {address?.address2 ? address?.address2 : null}
                </ShippingAddress>
              </div>
              <div className="bundleapp-subscription-list-item__shipping-address-last">
                <ShippingAddress>
                  {address?.city}, {address?.provinceCode} {address?.zip}{' '}
                  {address?.country}{' '}
                </ShippingAddress>
              </div>
            </>
          )}
        </Row2Col2>
        <Row2Col3 className="bundleapp-subscription-list-item__r2c3">
          <Button filled onClick={navigateToManageSubscription}>
            {t('subscription.manage')}
          </Button>
        </Row2Col3>
      </Row2>
    </ListItem>
  );
};

// TODO: clean and anstract these styles
// global vars instead of local ones
const ListItem = styled.div`
  --subscriptionItemBorder: 1px solid #dfe3e8;
  --subscriptionItemPaddingTop: 27px;
  --subscriptionItemPaddingRight: 21px;
  --subscriptionItemPaddingBottom: 18px;
  --subscriptionItemMarginBottom: 34px;
  border: var(--subscriptionItemBorder);
  padding-top: var(--subscriptionItemPaddingTop);
  padding-right: var(--subscriptionItemPaddingRight);
  padding-bottom: var(--subscriptionItemPaddingBottom);
  margin-bottom: var(--subscriptionItemMarginBottom);
  /* FIRST ROW: Column One: it has the month and day */
  --r1c1FlexGrow: 1;
  --billingDayFontWeight: bold;
  --billingDayFontSize: max(1em, 20px);
  --billingDayLineHeight: max(1em, 23px);
  --billingMonthFontSize: max(1em, 14px);
  --billingMonthLineHeight: max(1em, 16px);
  --productTitleFontWeight: bold;
  --productTitleFontSize: max(1em, 18px);
  --productTitleLineHeight: max(1em, 21px);
  --productVariantQtyFontSize: max(1em, 12px);
  --productVariantQtyLineHeight: max(1em, 16px);

  /* FIRST ROW: Column Two: it has the product image, the title, variant title, qty */
  --r1c2FlexGrow: 8;
  --r1c2BorderBottom: 1px solid #dfe3e8;
  --variantThumbWidth: 40px;
  --variantThumbHeight: 40px;
  --variantThumbBorderRadius: 8px;

  /* FIRST ROW: Column Three:  it has the price and delivery frequency */
  --r1c3FlexGrow: 2;
  --r1c3TextAlign: end;
  --r1c3BorderBottom: 1px solid #dfe3e8;
  --subscriptionTotalPriceFontSize: max(1em, 14px);
  --subscriptionTotalPriceLineHeight: max(1em, 20px);
  --deliveryFrequencyFontSize: max(1em, 14px);
  --deliveryFrequencyLineHeight: max(1em, 20px);

  /* SECOND ROW: Column One: it has nothing for now */
  --r2c1FlexGrow: 1;

  /* SECOND ROW: Column Two: it has shipping address */
  --r2c2FlexGrow: 8;
  --shippingAddressFontSize: max(1em, 12px);
  --shippingAddressLineHeight: max(1em, 14px);
  --shippingAddressFontWeight: bold;

  /* SECOND ROW: Column Three: it has manage that specific subscription button */
  --r2c3FlexGrow: 2;
  --r2c3TextAlign: end;
  --cancelledPausedStatusTextTransform: capitalize;
  --cancelledPausedStatusFontSize: max(1em, 12px);
  --cancelledPausedStatusLineHeight: max(1em, 14px);
  --manageButtonTextTransform: capitalize;
`;

const Row1 = styled.div`
  display: flex;
  margin-bottom: 24px;

  @media screen and (max-width: ${Breakpoints.sm}) {
    flex-wrap: wrap;
    margin-left: 24px;
  }
`;

const Row2 = styled.div`
  display: flex;

  @media screen and (max-width: ${Breakpoints.sm}) {
    flex-wrap: wrap;
    margin-left: 24px;
  }
`;

const Row1Col1 = styled.div`
  flex-grow: var(--r1c1FlexGrow);
  flex-basis: 0;
  text-align: center;

  @media screen and (max-width: ${Breakpoints.sm}) {
    order: 1;
    width: 50%;
    flex-grow: unset;
    flex-basis: unset;
    text-align: start;
  }
`;

const BillingDay = styled.div`
  font-weight: var(--billingDayFontWeight);
  font-size: var(--billingDayFontSize);
  line-height: var(--billingDayLineHeight);
`;
const BillingMonth = styled.div`
  font-size: var(--billingMonthFontSize);
  line-height: var(--billingMonthLineHeight);
`;

const Row1Col2 = styled.div`
  flex-grow: var(--r1c2FlexGrow);
  flex-basis: 0;
  border-bottom: var(--r1c2BorderBottom);

  @media screen and (max-width: ${Breakpoints.sm}) {
    order: 3;
    width: 100%;
    flex-grow: unset;
    flex-basis: unset;
    margin-top: 24px;
  }
`;

const Row1Col3 = styled.div`
  flex-grow: var(--r1c3FlexGrow);
  flex-basis: 0;
  border-bottom: var(--r1c3BorderBottom);
  text-align: var(--r1c3TextAlign);

  @media screen and (max-width: ${Breakpoints.sm}) {
    order: 2;
    width: 50%;
    flex-grow: unset;
    flex-basis: unset;
    border-bottom: unset;
  }
`;

const NotActiveStatus = styled.span`
  text-transform: var(--cancelledPausedStatusTextTransform);
  font-size: var(--cancelledPausedStatusFontSize);
  line-height: var(--cancelledPausedStatusLineHeight);
`;

const TotalPrice = styled.div`
  font-size: var(--subscriptionTotalPriceFontSize);
  line-height: var(--subscriptionTotalPriceLineHeight);
`;

const DeliveryFrequency = styled.div`
  font-size: var(--deliveryFrequencyFontSize);
  line-height: var(--deliveryFrequencyLineHeight);
`;

const Row2Col1 = styled.div`
  flex-grow: var(--r2c1FlexGrow);
  flex-basis: 0;

  @media screen and (max-width: ${Breakpoints.sm}) {
    order: 3;
    width: 100%;
    display: none;
    flex-grow: unset;
    flex-basis: unset;
  }
`;

const Row2Col2 = styled.div`
  display: flex;
  flex-grow: var(--r2c2FlexGrow);
  flex-basis: 0;

  @media screen and (max-width: ${Breakpoints.sm}) {
    order: 1;
    width: 70%;
    flex-grow: unset;
    flex-basis: unset;
    flex-direction: column;
  }
`;

const ShippingAddress = styled.span`
  font-size: var(--shippingAddressFontSize);
  line-height: var(--shippingAddressLineHeight);
  font-weight: var(--shippingAddressFontWeight);
  padding: 0 4px;
`;

const Row2Col3 = styled.div`
  flex-grow: var(--r2c3FlexGrow);
  flex-basis: 0;
  text-align: var(--r2c3TextAlign);

  @media screen and (max-width: ${Breakpoints.sm}) {
    order: 2;
    width: 30%;
    flex-grow: unset;
    flex-basis: unset;
    align-self: flex-end;
  }
`;
